import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';

/**
 * Landing Page - Light/Violet Theme
 * Modern, SEO-optimized landing page for event organizers
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  
  // SEO Meta Tags
  useSEO({
    title: 'Musique Automatisée pour Événements Sans DJ | DJ Nomad',
    description: 'Gérez la musique de vos mariages, soirées et événements sans DJ. Fonctionne offline, QR code pour suggestions invités, transitions automatiques. 100% Gratuit.',
    canonical: 'https://djnomad.app/',
    ogTitle: 'DJ Nomad - Musique Automatisée pour Vos Événements',
    ogDescription: 'Automatisez la musique de vos mariages et événements. Fonctionne sans Internet, vos invités suggèrent des titres via QR code. Gratuit.'
  });

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-in-section').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white sticky top-0 z-50 border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="flex justify-between items-center py-3">
            <Link to="/" className="flex items-center gap-2 text-violet-600 font-extrabold text-lg sm:text-xl">
              <span className="text-2xl sm:text-3xl">🎧</span>
              <span>DJ Nomad</span>
            </Link>
            
            <ul className="hidden md:flex items-center gap-6 lg:gap-8">
              <li><a href="#features" className="text-gray-600 hover:text-violet-600 font-medium transition-colors">Fonctionnalités</a></li>
              <li><a href="#use-cases" className="text-gray-600 hover:text-violet-600 font-medium transition-colors">Cas d'usage</a></li>
              <li><a href="#pricing" className="text-gray-600 hover:text-violet-600 font-medium transition-colors">Tarifs</a></li>
              <li><Link to="/blog" className="text-gray-600 hover:text-violet-600 font-medium transition-colors">Blog</Link></li>
              <li>
                <Link to="/login" className="text-violet-600 font-semibold px-4 py-2 border border-violet-600 rounded-full hover:bg-violet-600 hover:text-white transition-all">
                  Se connecter
                </Link>
              </li>
            </ul>
            
            <Link to="/login" className="bg-violet-600 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-semibold hover:bg-violet-700 transition-all text-sm sm:text-base">
              Essayer Gratuitement
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-violet-50 via-white to-purple-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-gray-900 leading-tight mb-6">
            La musique de votre événement,<br className="hidden sm:block" />
            <span className="text-violet-600">automatisée et sans DJ</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-violet-600 font-semibold mb-4">
            Fini les DJ hors de prix. Fini le stress technique.
          </p>
          
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Gérez la musique de vos mariages, soirées d'entreprise et événements privés 
            en toute simplicité. Fonctionne sans Internet, vos invités suggèrent des titres, 
            les transitions sont automatiques.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8">
            <Link to="/login" className="bg-violet-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-violet-700 transition-all text-base sm:text-lg shadow-lg hover:shadow-xl">
              Préparer mon premier événement →
            </Link>
            <a href="#features" className="bg-white text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold border-2 border-gray-300 hover:border-violet-600 hover:text-violet-600 transition-all text-base sm:text-lg">
              Voir comment ça marche
            </a>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
            {['100% Gratuit', 'Sans publicité', 'Fonctionne offline', 'Aucune installation'].map((badge) => (
              <span key={badge} className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 border border-gray-200 shadow-sm">
                ✓ {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-12 bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-black mb-2">800-1500€</div>
              <div className="text-violet-200 font-medium">Économisés sur le DJ</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-black mb-2">10 min</div>
              <div className="text-violet-200 font-medium">Setup complet</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-black mb-2">100%</div>
              <div className="text-violet-200 font-medium">Fonctionne offline</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-center text-gray-900 mb-4">
            Pourquoi les organisateurs d'événements adorent DJ Nomad
          </h2>
          <p className="text-center text-gray-600 text-base sm:text-lg mb-12 max-w-2xl mx-auto">
            Tout ce dont vous avez besoin pour gérer la musique professionnellement, sans les compétences d'un DJ
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '📱', title: 'Fonctionne Sans Internet', desc: 'Mode offline garanti. WiFi instable en salle ? Connexion coupée ? Votre musique continue sans interruption.' },
              { icon: '🎵', title: 'QR Code pour les Invités', desc: 'Vos invités scannent un QR code et suggèrent leurs titres préférés en temps réel. Vous validez ou refusez.' },
              { icon: '⚡', title: 'Transitions Automatiques', desc: 'Pas besoin de savoir mixer. Les transitions entre morceaux sont fluides et automatiques.' },
              { icon: '📋', title: 'Gestion par Parties', desc: 'Organisez votre événement en parties : cocktail, dîner, danse. Playlists différentes pour chaque moment.' },
              { icon: '💰', title: 'Économisez 800-1500€', desc: 'Un DJ professionnel coûte entre 800€ et 1500€. DJ Nomad est 100% gratuit.' },
              { icon: '☁️', title: 'Stockage Cloud Sécurisé', desc: '2 GB gratuits pour vos morceaux (~400 chansons). Synchronisation automatique.' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200 hover:border-violet-300 hover:shadow-lg hover:-translate-y-1 transition-all">
                <span className="text-4xl sm:text-5xl block mb-4">{feature.icon}</span>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-center text-gray-900 mb-4">
            Comment ça marche ?
          </h2>
          <p className="text-center text-gray-600 text-base sm:text-lg mb-12 max-w-2xl mx-auto">
            Trois étapes pour gérer la musique de votre événement comme un pro
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
            {[
              { num: '1', title: 'Créez votre événement', desc: 'Définissez les parties (cocktail, dîner, danse). Uploadez vos morceaux. Tout fonctionne dans votre navigateur.' },
              { num: '2', title: 'Partagez le QR code', desc: 'Imprimez le QR code sur vos tables. Vos invités suggèrent leurs titres favoris. Vous restez maître.' },
              { num: '3', title: 'Profitez de votre soirée', desc: 'DJ Nomad gère les transitions automatiquement. La musique continue même sans Internet.' },
            ].map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-violet-600 to-purple-600 text-white rounded-full flex items-center justify-center text-2xl sm:text-3xl font-black mx-auto mb-4 shadow-lg">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-center text-gray-900 mb-4">
            Parfait pour tous vos événements
          </h2>
          <p className="text-center text-gray-600 text-base sm:text-lg mb-12 max-w-2xl mx-auto">
            De l'intimité d'un mariage aux grandes soirées corporate
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '💍', title: 'Mariages & Anniversaires', desc: 'Laissez vos invités contribuer à l\'ambiance musicale sans perdre le contrôle.', benefit: 'Économisez 800-1500€' },
              { icon: '🏢', title: 'Événements d\'Entreprise', desc: 'Soirées séminaires, cocktails clients, team buildings. Fiabilité offline garantie.', benefit: 'Setup en 10 minutes' },
              { icon: '🍽️', title: 'Traiteurs & Venues', desc: 'Proposez un service musique à vos clients sans matériel supplémentaire.', benefit: 'Service premium gratuit' },
              { icon: '🎪', title: 'Festivals & Marchés', desc: 'WiFi absent ou instable ? Aucun problème. 100% offline.', benefit: 'Zéro dépendance Internet' },
            ].map((useCase, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border border-gray-200 hover:border-violet-300 hover:shadow-lg hover:-translate-y-1 transition-all">
                <span className="text-4xl block mb-4">{useCase.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{useCase.desc}</p>
                <p className="text-violet-600 font-semibold text-sm">→ {useCase.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-center text-gray-900 mb-4">
            Tarification Simple et Transparente
          </h2>
          <p className="text-center text-gray-600 text-base sm:text-lg mb-12">
            Commencez gratuitement. Passez en Pro quand vous êtes prêt.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {/* Free Plan */}
            <div className="relative bg-white p-6 sm:p-8 rounded-2xl border-2 border-violet-600 shadow-xl">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                RECOMMANDÉ
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Gratuit</h3>
              <div className="text-4xl sm:text-5xl font-black text-violet-600 mb-1">0€</div>
              <div className="text-gray-500 mb-6">Pour toujours</div>
              
              <ul className="space-y-3 mb-8">
                {['3 événements simultanés', '2 GB stockage cloud', 'Mode offline complet', 'QR code invités (50 suggestions)', 'Transitions automatiques', 'Gestion par parties', 'Support communautaire'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-700">
                    <span className="text-green-500 font-bold">✓</span> {item}
                  </li>
                ))}
              </ul>
              
              <Link to="/login" className="block w-full bg-violet-600 text-white text-center py-3 rounded-full font-semibold hover:bg-violet-700 transition-all">
                Commencer Gratuitement
              </Link>
            </div>
            
            {/* Pro Plan */}
            <div className="bg-gray-50 p-6 sm:p-8 rounded-2xl border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Pro</h3>
              <div className="text-4xl sm:text-5xl font-black text-gray-900 mb-1">19€</div>
              <div className="text-gray-500 mb-6">par mois</div>
              
              <ul className="space-y-3 mb-8">
                {['Tout du plan Gratuit', 'Événements illimités', '100 GB stockage cloud', 'Suggestions illimitées', 'Branding personnalisé', 'Export analytics', 'Support prioritaire'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-700">
                    <span className="text-green-500 font-bold">✓</span> {item}
                  </li>
                ))}
              </ul>
              
              <button disabled className="block w-full bg-gray-300 text-gray-600 text-center py-3 rounded-full font-semibold cursor-not-allowed">
                Bientôt Disponible
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black mb-4">
            Prêt à automatiser la musique<br />de votre prochain événement ?
          </h2>
          <p className="text-lg sm:text-xl text-violet-100 mb-8">
            Gratuit. Sans pub. Fonctionne sans Internet. Aucune installation requise.
          </p>
          <Link to="/login" className="inline-block bg-white text-violet-600 px-8 sm:px-10 py-4 rounded-full font-bold text-lg hover:bg-violet-50 transition-all shadow-lg">
            Commencer gratuitement →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-xl font-bold mb-4">🎧 DJ Nomad</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                La solution de musique automatisée pour vos événements. Fonctionne sans Internet, gratuit, sans pub.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Produit</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-gray-400 hover:text-violet-400 transition-colors">Fonctionnalités</a></li>
                <li><a href="#use-cases" className="text-gray-400 hover:text-violet-400 transition-colors">Cas d'usage</a></li>
                <li><Link to="/faq" className="text-gray-400 hover:text-violet-400 transition-colors">FAQ</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-violet-400 transition-colors">Blog</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Entreprise</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-violet-400 transition-colors">À propos</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-violet-400 transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Légal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-violet-400 transition-colors">Confidentialité</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-violet-400 transition-colors">CGU</Link></li>
                <li><Link to="/legal" className="text-gray-400 hover:text-violet-400 transition-colors">Mentions légales</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2025 DJ Nomad. Tous droits réservés. Créé avec ❤️ pour les organisateurs d'événements.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
